import type { ReactNode } from 'react'

/**
 * Wrap the parts of `text` that match `query` in a <mark>.
 *
 * Used by every surface that renders server-side search results (global search,
 * chart queue, history) so a hit is visible in the row rather than leaving the
 * reader to work out WHY it matched.
 *
 * Highlighting is purely presentational — it re-finds the term in the text the
 * server already decided to return. It never implies the server matched on that
 * particular column, and a matched row whose term is not literally present
 * (an encrypted exact-index hit on a masked value, say) simply renders plain.
 */
export function HighlightMatch({ text, query }: { text: string; query: string }): ReactNode {
  const term = query.trim()
  // One character matches nearly everything, so the marks become noise.
  if (!term || term.length < 2 || !text) {
    return <>{text}</>
  }

  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // `split` with a capturing group yields [before, match, between, match, …],
  // so the MATCHES are exactly the odd indices. Testing each part against a /g
  // regex instead would be wrong: /g regexes carry `lastIndex` between calls,
  // so alternating .test() calls skip matches non-deterministically.
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  if (parts.length === 1) {
    return <>{text}</>
  }

  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="search-hit">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}
